import { CalendarCustomWeek } from '@molecules/CalendarCustomWeek';
import {
  ListCalendarViewToggle,
  ViewMode,
} from '@molecules/ListCalendarViewToggle';
import { SessionFilter } from '@molecules/SessionFilter';
import {
  logout,
  useBulkGetProfiles,
  useGetBulkSessionData,
  useGetCanonicalCategoriesByEvent,
  useGetCanonicalTagsByEvent,
  useGetCommunity,
  useGetEvent,
  useGetSessionsByEventId,
  useGetSessionsByTagsAndCategories,
  useGetUserViewedSessions,
  useIsEventSpeakerOrModerator,
} from '@sunrise-ui/api-client';
import {
  SessionDetailsModal,
  SpeakerFilter,
  withAuthGate,
} from '@sunrise-ui/composites';

import { LogoIcon } from '@assets/images';
import { ArrowLeft01Icon, FilterIcon } from '@hugeicons/core-free-icons';
import { SessionCardList } from '@organisms/SessionCardList';
import { SessionStatus } from '@sunrise-ui/api/events/api';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Button,
  ButtonVariants,
  Colors,
  ExternalLink,
  Header,
  IconButton,
  Loader,
  PageContainer,
  TextReg,
  Toggle,
  calendarTheme,
  isNative,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  useSafeLocalSearchParams,
  useSetHeader,
} from '@sunrise-ui/primitives';
import { groupSessionsByDate } from '@utils/sessionList';
import { Redirect, router } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { CalendarProvider, Timeline } from 'react-native-calendars/src';
import type { Event as CalendarEvent } from 'react-native-calendars/src/timeline/EventBlock';
import type { MarkedDates } from 'react-native-calendars/src/types';
import { Portal } from 'react-native-paper';

function ScheduledSessions() {
  const { isMobile } = useMediaQueries();
  const { eventId, fromEvents, view, date } = useSafeLocalSearchParams<{
    eventId: string;
    fromEvents?: string;
    view?: ViewMode;
    date?: string;
  }>();

  // Determine responsive layout flags - condensed view at 767px and below
  const isCondensedView = isMobile; // <= 767px only

  const styles = useDynamicStyles(createStyles, { isMobile });
  const { isAuthenticated, currentUserId, isAdmin, openAuthFlowModal } =
    useAuthStore();

  // Fetch event data to display event name
  const { data: event } = useGetEvent(eventId);
  const communityId = event?.community_id;

  // Fetch community data to get slug for navigation
  const { data: community } = useGetCommunity(communityId);
  const [filterCategoryIds, setFilterCategoryIds] = useState<Set<string>>(
    new Set(),
  );
  const [filterTagIds, setFilterTagIds] = useState<Set<string>>(new Set());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showViewedOnly, setShowViewedOnly] = useState(false);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<
    string | undefined
  >();

  // Gate: allow only speakers/moderators of scheduled sessions to see this page
  const { isAllowed, isLoading: isLoadingGate } = useIsEventSpeakerOrModerator(
    eventId,
    currentUserId,
    { enabled: isAuthenticated && !isAdmin },
  );

  // Fetch previously viewed sessions for the current user and event
  const { data: viewedSessions, isSuccess: isViewedSessionsSuccess } =
    useGetUserViewedSessions(eventId, currentUserId);

  // Compute viewedSessionIds from API results only (no local state needed)
  const viewedSessionIds = useMemo(() => {
    const viewedIds = new Set<string>();

    if (
      isViewedSessionsSuccess &&
      viewedSessions &&
      viewedSessions.length > 0
    ) {
      viewedSessions.forEach((session: { id: string | undefined }) => {
        if (session.id) viewedIds.add(session.id as string);
      });
    }

    return viewedIds;
  }, [isViewedSessionsSuccess, viewedSessions]);

  // Pre-cache canonical tag and category data to prevent redundant API calls
  // when opening SessionDetailsModal (which uses useGetSessionTagsAndCategories)
  const { data: tags, isLoading: isLoadingTags } =
    useGetCanonicalTagsByEvent(eventId);
  const { data: categories, isLoading: isLoadingCategories } =
    useGetCanonicalCategoriesByEvent(eventId);

  // Get all available filter options (both categories and tags)
  const allFilterOptions = [
    ...(categories
      ?.map((cat) => cat.id)
      .filter((id): id is string => Boolean(id)) ?? []),
    ...(tags?.map((tag) => tag.id).filter((id): id is string => Boolean(id)) ??
      []),
  ];

  const isLoadingFilterData = isLoadingTags || isLoadingCategories;
  const hasFilter = filterCategoryIds.size > 0 || filterTagIds.size > 0;

  const { data: filteredSessions } = useGetSessionsByTagsAndCategories(
    {
      category_ids: [...filterCategoryIds],
      tag_ids: [...filterTagIds],
      match_all_categories: false,
      match_all_tags: false,
    },
    eventId,
  );

  // Fetch sessions by event ID without shuffle for scheduled listing
  const { data: allSessions, isLoading: isLoadingSessions } =
    useGetSessionsByEventId(eventId, false, communityId);

  // Filter for only scheduled/confirmed sessions and sort by start_date ascending
  const sessions = useMemo(() => {
    if (!allSessions) return undefined;
    return allSessions
      .filter(
        (session) =>
          session.status === SessionStatus.Scheduled ||
          session.status === SessionStatus.Confirmed,
      )
      .sort((a, b) => {
        const aTs = a.start_date
          ? DateTime.fromISO(a.start_date).toMillis()
          : Number.MAX_SAFE_INTEGER;
        const bTs = b.start_date
          ? DateTime.fromISO(b.start_date).toMillis()
          : Number.MAX_SAFE_INTEGER;
        return aTs - bTs;
      });
  }, [allSessions]);

  // Extract session IDs for bulk data fetch
  const sessionIds = sessions?.map((session) => session.id) || [];

  // Fetch bulk data for all sessions
  const {
    data: bulkSessionData,
    isLoading: isLoadingBulkData,
    isSuccess: isBulkDataSuccess,
  } = useGetBulkSessionData({
    sessionIds,
  });

  // Extract user_ids from bulk session data
  const user_ids =
    bulkSessionData?.session_data
      ?.flatMap(
        (sessionData) =>
          sessionData.data.attendees?.map((attendee) => attendee.user_id) || [],
      )
      .filter(Boolean) || [];

  // Fetch bulk user data for all users
  const { isLoading: isLoadingProfiles } = useBulkGetProfiles(user_ids);

  // Create filter controls (filter button, speaker search, toggle)
  const createFilterControls = () => {
    // Check if any filters are applied and count them
    let activeFilterCount = filterCategoryIds.size + filterTagIds.size;

    // Add 1 for viewed toggle if it's set to "viewed" (true)
    if (showViewedOnly) {
      activeFilterCount += 1;
    }

    // Add 1 for speaker filter if a speaker is selected
    if (selectedSpeakerId) {
      activeFilterCount += 1;
    }

    const hasActiveFilters = activeFilterCount > 0;

    // Conditional back button when navigated from events screen
    const backButton =
      fromEvents === 'true' ? (
        <IconButton
          icon={ArrowLeft01Icon}
          size={isMobile ? 'small' : 'medium'}
          backgroundColor="transparent"
          onPress={() => {
            router.navigate('/events');
          }}
        />
      ) : null;

    const filterButton = (
      <View style={styles.filterButtonContainer}>
        <IconButton
          icon={FilterIcon}
          size={isMobile ? 'small' : 'medium'}
          loading={isLoadingFilterData}
          disabled={isLoadingFilterData || !allFilterOptions.length}
          backgroundColor={hasActiveFilters ? Colors.opacity20 : 'transparent'}
          onPress={() => {
            setIsFilterOpen(true);
          }}
        />
        {hasActiveFilters && (
          <View style={styles.filterCountBadge}>
            <TextReg style={styles.filterCountText}>
              {activeFilterCount}
            </TextReg>
          </View>
        )}
      </View>
    );

    const speakerDropdown = (
      <SpeakerFilter
        eventId={eventId}
        selectedSpeakerId={selectedSpeakerId}
        onSpeakerSelect={setSelectedSpeakerId}
      />
    );

    // Create toggle component for viewed/unviewed filter
    const toggleComponent = isAuthenticated ? (
      <Toggle
        value={showViewedOnly}
        onValueChange={setShowViewedOnly}
        leftLabel="All"
        rightLabel="Viewed"
      />
    ) : null;

    return { backButton, filterButton, speakerDropdown, toggleComponent };
  };

  // Create desktop header controls layout
  const createDesktopControls = () => {
    const { backButton, filterButton, speakerDropdown } =
      createFilterControls();

    return (
      <View style={styles.headerLeftContainer}>
        {backButton}
        {filterButton}
        {speakerDropdown}
      </View>
    );
  };

  const loginButton = isAuthenticated ? (
    <TouchableOpacity onPress={logout}>
      <TextReg style={styles.loginText}>Log Out</TextReg>
    </TouchableOpacity>
  ) : (
    <TouchableOpacity
      style={styles.loginLink}
      onPress={() => {
        openAuthFlowModal({ isDismissible: true }, { currentStep: 'login' });
      }}
    >
      <TextReg style={styles.loginText}>Log In</TextReg>
    </TouchableOpacity>
  );

  // Get filter controls for different view types
  const { toggleComponent: headerToggle } = createFilterControls();

  // Create tickets button component
  const handleTicketsPress = async () => {
    await WebBrowser.openBrowserAsync(
      `https://checkout.joincommuniful.com/events/${eventId}`,
      {
        windowFeatures: {
          popup: false,
        },
      },
    );
  };

  const ticketsButton = (
    <Button
      variant={ButtonVariants.PURPLE}
      size={isMobile ? 'small' : 'medium'}
      onPress={handleTicketsPress}
      labelStyle={styles.ticketsButton}
    >
      Tickets
    </Button>
  );

  // Set up header with responsive layout and height
  useSetHeader(
    !isCondensedView
      ? {
          header: () => (
            <Header
              headerLeft={
                <View style={styles.headerLeftContainer}>
                  {createDesktopControls()}
                </View>
              }
              headerRight={
                <View style={styles.headerRightContainer}>
                  {headerToggle}
                  {loginButton}
                  {ticketsButton}
                </View>
              }
              desktopLogo={LogoIcon}
              mobileLogo={LogoIcon}
              showShadow={true}
            />
          ),
        }
      : {
          header: () => (
            <Header
              headerLeft={
                <View style={styles.headerLeftContainer}>
                  {createFilterControls().backButton}
                  {createFilterControls().filterButton}
                </View>
              }
              headerRight={
                <View style={styles.headerRightContainer}>
                  {loginButton}
                  {ticketsButton}
                </View>
              }
              desktopLogo={LogoIcon}
              mobileLogo={LogoIcon}
              showShadow={true} // Enable yellow bottom shadow
            />
          ),
        },
  );

  // Apply speaker filter and viewed filter to sessions (already sorted by start date)
  const filteredBySpokenSessions = useMemo(() => {
    if (!selectedSpeakerId || !bulkSessionData?.session_data) {
      return sessions;
    }

    return sessions?.filter((session) => {
      const sessionData = bulkSessionData.session_data?.find(
        (data) => data.session_id === session.id,
      );

      if (!sessionData?.data.attendees) return false;

      return sessionData.data.attendees.some(
        (attendee) => attendee.user_id === selectedSpeakerId,
      );
    });
  }, [sessions, selectedSpeakerId, bulkSessionData]);

  const sortedSessions = useMemo(() => {
    if (!showViewedOnly || !isAuthenticated) {
      return filteredBySpokenSessions;
    }
    return filteredBySpokenSessions?.filter((session) =>
      viewedSessionIds.has(session.id as string),
    );
  }, [
    filteredBySpokenSessions,
    showViewedOnly,
    isAuthenticated,
    viewedSessionIds,
  ]);

  // Sort and filter the tag/category filtered sessions by start date as well
  const baseSortedFilteredSessions = useMemo(() => {
    const list = filteredSessions?.sessions;
    if (!list) return list;
    return [...list]
      .filter(
        (s) =>
          s.status === SessionStatus.Scheduled ||
          s.status === SessionStatus.Confirmed,
      )
      .sort((a, b) => {
        const aTs = a.start_date
          ? DateTime.fromISO(a.start_date).toMillis()
          : Number.MAX_SAFE_INTEGER;
        const bTs = b.start_date
          ? DateTime.fromISO(b.start_date).toMillis()
          : Number.MAX_SAFE_INTEGER;
        return aTs - bTs;
      });
  }, [filteredSessions?.sessions]);

  const filteredBySpokenFilteredSessions = useMemo(() => {
    if (!selectedSpeakerId || !bulkSessionData?.session_data) {
      return baseSortedFilteredSessions;
    }

    return baseSortedFilteredSessions?.filter((session) => {
      const sessionData = bulkSessionData.session_data?.find(
        (data) => data.session_id === session.id,
      );

      if (!sessionData?.data.attendees) return false;

      return sessionData.data.attendees.some(
        (attendee) => attendee.user_id === selectedSpeakerId,
      );
    });
  }, [baseSortedFilteredSessions, selectedSpeakerId, bulkSessionData]);

  const sortedFilteredSessions = useMemo(() => {
    if (!showViewedOnly || !isAuthenticated) {
      return filteredBySpokenFilteredSessions;
    }
    return filteredBySpokenFilteredSessions?.filter((session) =>
      viewedSessionIds.has(session.id as string),
    );
  }, [
    filteredBySpokenFilteredSessions,
    showViewedOnly,
    isAuthenticated,
    viewedSessionIds,
  ]);

  const groupedSessions = useMemo(
    () =>
      groupSessionsByDate(
        hasFilter ? sortedFilteredSessions : sortedSessions,
        'asc',
      ),
    [hasFilter, sortedFilteredSessions, sortedSessions, isMobile],
  );

  // Marked dates for the calendar dots in section headers
  const markedDates: MarkedDates = useMemo(() => {
    const marked: MarkedDates = {};
    const list = hasFilter ? sortedFilteredSessions : sessions;
    if (!list) return marked;
    for (const session of list) {
      const { start_date } = session;
      if (!start_date) continue;
      const dateKey = DateTime.fromISO(start_date).toISODate();
      if (!dateKey) continue;
      if (!marked[dateKey]) {
        marked[dateKey] = {
          marked: true,
          dotColor: Colors.pink0,
          dots: [{ color: Colors.pink0 }],
        };
      } else if (marked[dateKey].dots && marked[dateKey].dots!.length < 4) {
        marked[dateKey] = {
          ...marked[dateKey],
          dots: [...(marked[dateKey].dots ?? []), { color: Colors.pink0 }],
        };
      }
    }
    return marked;
  }, [hasFilter, sortedFilteredSessions, sessions]);

  const firstDateISO = DateTime.fromISO(
    groupedSessions[0]?.startDateTime,
  ).toISODate();
  const [selectedDate, setSelectedDate] = useState(firstDateISO);

  // Keep selectedDate in sync with the `date` search param and the first available date
  useEffect(() => {
    const parsed = DateTime.fromISO(date ?? '');
    const iso = parsed.isValid ? parsed.toISODate() : firstDateISO;
    setSelectedDate(iso);
  }, [date, firstDateISO]);

  // Always-valid ISO date string and DateTime for calendar components
  const selectedDateISO = useMemo(() => {
    if (selectedDate) {
      const parsed = DateTime.fromISO(selectedDate);
      if (parsed.isValid) return parsed.toISODate() as string;
    }
    if (firstDateISO) return firstDateISO;
    return DateTime.now().toISODate() as string;
  }, [selectedDate, firstDateISO]);

  const selectedDateDT = useMemo(
    () => DateTime.fromISO(selectedDateISO) as unknown as DateTime<true>,
    [selectedDateISO],
  );

  const { timelineEvents, timelineRange } = useMemo(() => {
    const list = hasFilter ? sortedFilteredSessions : sortedSessions;
    if (!list)
      return {
        timelineEvents: [] as CalendarEvent[],
        timelineRange: { start: 0, end: 24 },
      };

    let earliestHour = 24;
    let latestHour = 0;

    const events: CalendarEvent[] = list
      .filter((s) => {
        const start = DateTime.fromISO(s.start_date ?? '');
        const end = s.end_date
          ? DateTime.fromISO(s.end_date)
          : start.plus({ minutes: 60 });
        if (!start.isValid || !end.isValid) return false;
        const dayStart = selectedDateDT.startOf('day');
        const dayEnd = selectedDateDT.endOf('day');
        // Include if the session overlaps the selected day
        return end >= dayStart && start <= dayEnd;
      })
      .map((s): CalendarEvent => {
        const startDate = DateTime.fromISO(s.start_date ?? '');
        const endDate = s.end_date
          ? DateTime.fromISO(s.end_date)
          : startDate.plus({ minutes: 60 });

        const isStartingToday = startDate.hasSame(selectedDateDT, 'day');
        const isEndingToday = endDate.hasSame(selectedDateDT, 'day');

        if (!isStartingToday) earliestHour = 0;
        else if (startDate.hour < earliestHour) earliestHour = startDate.hour;
        if (!isEndingToday) latestHour = 24;
        else if (endDate.hour > latestHour) latestHour = endDate.hour;

        const start = isStartingToday
          ? startDate
          : selectedDateDT.startOf('day');
        const end = isEndingToday ? endDate : selectedDateDT.endOf('day');

        return {
          id: s.id,
          start: start.toFormat('yyyy-MM-dd HH:mm:ss'),
          end: end.toFormat('yyyy-MM-dd HH:mm:ss'),
          title: s.topic_name,
          color: 'transparent',
        } as CalendarEvent;
      });

    const range = {
      start: Math.max(0, earliestHour - 1),
      end: Math.min(24, latestHour + 2),
    };

    return { timelineEvents: events, timelineRange: range };
  }, [hasFilter, sortedFilteredSessions, sortedSessions, selectedDateDT]);
  // Define a comprehensive loading state that includes all data fetching operations
  const isLoading =
    isLoadingSessions ||
    (sessionIds.length > 0 && (isLoadingBulkData || isLoadingProfiles)) ||
    !isBulkDataSuccess;

  // After all hooks are mounted, enforce the role-based gate with stable hook order
  if (!isAdmin && isLoadingGate) {
    return (
      <Portal.Host>
        <PageContainer>
          <Loader style={styles.loader} />
        </PageContainer>
      </Portal.Host>
    );
  }

  if (!isAdmin && !isLoadingGate && !isAllowed) {
    return <Redirect href="/" />;
  }

  // Redirect to home page if no sessions are available
  if (!isLoading && !sessions?.length) {
    return <Redirect href="/" />;
  }

  return (
    <View style={styles.container}>
      {event?.name && (
        <View style={styles.pageHeaderContainer}>
          <View style={[styles.eventNameContainer, styles.eventHeaderCompact]}>
            <TextReg style={styles.eventNameText}>{event.name}</TextReg>
            {event.description && (
              <View style={styles.eventDescriptionContainer}>
                <TextReg style={styles.eventDescriptionText}>
                  {event.description}{' '}
                  <ExternalLink
                    href="https://www.tcstartupweek.com"
                    style={styles.learnMoreLinkContainer}
                  >
                    <TextReg style={styles.learnMoreLink}>Learn more</TextReg>
                  </ExternalLink>
                </TextReg>
              </View>
            )}
            <View style={styles.viewToggleContainer}>
              <ListCalendarViewToggle />
            </View>
          </View>
        </View>
      )}
      {view === ViewMode.CALENDAR ? (
        isLoading ? (
          <PageContainer>
            <Loader style={styles.loader} />
          </PageContainer>
        ) : (
          <View style={styles.calendarFullWidthContainer}>
            <CalendarProvider
              date={selectedDateISO}
              onDateChanged={(newDate) => {
                router.setParams({ date: newDate });
              }}
              showTodayButton
              theme={calendarTheme}
            >
              <CalendarCustomWeek
                compact
                selectedDate={selectedDateDT}
                onDateChanged={(newDate) => {
                  router.setParams({ date: newDate });
                }}
                markedDates={markedDates}
              />
              {timelineEvents.length === 0 ? (
                <View style={styles.emptyCalendarContainer}>
                  <TextReg style={styles.emptyStateText}>
                    There are no sessions scheduled for{' '}
                    {selectedDateDT.toFormat('EEEE, MMMM d')}
                  </TextReg>
                </View>
              ) : (
                <Timeline
                  date={selectedDateISO}
                  events={timelineEvents}
                  rightEdgeSpacing={0}
                  overlapEventsSpacing={0}
                  timelineLeftInset={isNative ? 64 : 70}
                  theme={calendarTheme}
                  start={timelineRange.start}
                  end={timelineRange.end}
                  format24h={false}
                  renderEvent={(calendarEvent) => {
                    const { id, title } = calendarEvent as CalendarEvent & {
                      id?: string;
                    };

                    // Handle session click with single session event logic
                    const handleSessionPress = () => {
                      if (!id) return;

                      // Check if this is a single session event
                      const isSingleSessionEvent = Boolean(
                        (sessions ?? []).length === 1,
                      );

                      if (isSingleSessionEvent && community?.slug) {
                        // Navigate to community page for single session events using slug
                        router.navigate(`/communities/${community.slug}`);
                      } else {
                        // Open session details modal for multi-session events
                        router.setParams({ sessionId: id });
                      }
                    };

                    return (
                      <TouchableOpacity
                        activeOpacity={0.85}
                        onPress={handleSessionPress}
                        style={styles.calendarEventTouchable}
                      >
                        <View
                          style={[
                            styles.calendarSessionItem,
                            styles.calendarSessionItemInternal,
                            { alignSelf: 'stretch' },
                          ]}
                        >
                          <View style={styles.calendarSessionRow}>
                            <TextReg
                              style={[styles.calendarSessionTitle, { flex: 1 }]}
                              numberOfLines={isMobile ? 3 : 2}
                            >
                              {title}
                            </TextReg>
                          </View>
                        </View>
                      </TouchableOpacity>
                    );
                  }}
                />
              )}
            </CalendarProvider>
          </View>
        )
      ) : (
        <View style={styles.pageContent}>
          <View style={styles.listContainer}>
            <SessionCardList
              sessions={sessions}
              isLoading={isLoadingSessions}
              scrollKey="sessions/[eventId]"
              userId={currentUserId}
            />
          </View>
        </View>
      )}

      <SessionDetailsModal communityId={communityId} />
      <SessionFilter
        filterCategoryIds={filterCategoryIds}
        setFilterCategoryIds={setFilterCategoryIds}
        filterTagIds={filterTagIds}
        setFilterTagIds={setFilterTagIds}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        selectedSpeakerId={selectedSpeakerId}
        setSelectedSpeakerId={setSelectedSpeakerId}
        showViewedOnly={showViewedOnly}
        setShowViewedOnly={setShowViewedOnly}
      />
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      width: '100%',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      minHeight: 0,
    },
    pageContent: {
      width: '100%',
      maxWidth: 800,
      alignSelf: 'center',
      flex: 1,
      minHeight: 0,
    },
    sectionList: {
      flex: 1,
      flexGrow: 1,
    },
    pageHeaderContainer: {
      width: '100%',
      maxWidth: 800,
      alignSelf: 'center',
      paddingHorizontal: isMobile ? 16 : 24,
    },
    eventNameContainer: {
      paddingTop: 12,
      alignItems: 'center',
      backgroundColor: Colors.purple0,
      marginBottom: 8,
    },
    eventHeaderCompact: {
      marginBottom: 0,
    },
    eventNameText: {
      fontSize: isMobile ? 24 : 28,
      textAlign: 'center',
      letterSpacing: 0.5,
    },
    eventDescriptionContainer: {
      marginTop: 8,
      paddingHorizontal: 8,
    },
    eventDescriptionText: {
      fontSize: isMobile ? 14 : 16,
      textAlign: 'center',
      lineHeight: isMobile ? 20 : 22,
    },
    viewToggleContainer: {
      marginTop: 8,
      alignItems: 'center',
      justifyContent: 'center',
    },
    learnMoreLinkContainer: {
      textDecorationLine: 'none',
    },
    learnMoreLink: {
      color: Colors.purple1,
    },
    filterButtonContainer: {
      position: 'relative',
    },
    filterCountBadge: {
      position: 'absolute',
      top: -4,
      right: -4,
      backgroundColor: Colors.pink0,
      width: 18,
      height: 18,
      borderRadius: 9,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1,
    },
    filterCountText: {
      color: '#FFFFFF',
      fontSize: 12,
      lineHeight: 14,
      fontWeight: '500',
    },
    sessionCard: {
      marginVertical: isMobile ? 12 : 16,
    },
    sessionRow: {
      flexDirection: 'row',
      justifyContent: 'center',
    },
    loginLink: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 8,
    },
    loginText: {
      color: Colors.text,
      fontSize: 14,
    },
    ticketsButton: {
      marginHorizontal: isMobile ? 0 : 24,
    },
    smallSpacer: {
      height: 16,
    },
    centeredHeaderContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    headerLeftContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    headerRightContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    speakerDropdown: {
      minWidth: 180,
      maxWidth: 240,
    },
    searchInputContainer: {
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'center',
      minWidth: 180,
      maxWidth: 240,
    },
    searchIcon: {
      position: 'absolute',
      left: 12,
      top: '50%',
      transform: [{ translateY: -10 }],
      zIndex: 1,
    },
    speakerSearchInput: {
      paddingLeft: 40,
      width: '100%',
      borderRadius: 8,
      backgroundColor: Colors.opacity05,
      textAlign: 'left',
    },
    dropdownMenu: {
      position: 'absolute',
      top: 45,
      left: 0,
      right: 0,
      backgroundColor: Colors.purple0,
      borderRadius: 8,
      maxHeight: 300,
      zIndex: 1000,
      elevation: 8,
      shadowColor: Colors.purple1,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      overflow: 'hidden',
    },
    dropdownItem: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: Colors.opacity10,
    },
    noResultsText: {
      textAlign: 'center',
    },
    selectedSpeakerContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: Colors.purple0,
      borderRadius: 24,
      height: 40,
      paddingHorizontal: 12,
      paddingLeft: 40,
      width: '100%',
    },
    selectedIconContainer: {
      position: 'absolute',
      left: 12,
      top: '50%',
      transform: [{ translateY: -10 }],
      zIndex: 1,
      height: 20,
      justifyContent: 'center',
    },
    selectedSpeakerText: {
      flex: 1,
      marginRight: 8,
    },
    clearButtonContainer: {
      width: 20,
      height: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
    clearText: {
      fontSize: 16,
      color: Colors.text,
    },
    activeFilterButton: {
      borderWidth: 2,
      borderColor: Colors.purple1,
    },
    cardsList: {
      flexGrow: 1,
      paddingBottom: 24,
      paddingHorizontal: 8,
    },
    listContainer: {
      flex: 1,
      minHeight: 0,
    },
    calendarFullWidthContainer: {
      flex: 1,
      width: '100%',
      backgroundColor: Colors.purple0,
      paddingTop: 0,
      gap: 0,
    },
    footerSpacer: {
      height: 100,
    },
    loader: {
      marginTop: 50,
    },
    emptyStateText: {
      textAlign: 'center',
      marginTop: 50,
      fontSize: isMobile ? 14 : 16,
    },
    emptyCalendarContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 32,
      paddingVertical: 64,
    },
    calendarSessionItem: {
      backgroundColor: Colors.purple0,
      flex: 1,
      height: '100%',
      borderRadius: 8,
      padding: 8,
      borderLeftWidth: 4,
      borderLeftColor: Colors.yellow0,
      borderColor: Colors.yellow0,
      borderWidth: 1,
      width: '100%',
      gap: 4,
      overflow: 'hidden',
    },
    calendarSessionItemDefault: {
      borderLeftColor: Colors.yellow0,
      borderColor: Colors.yellow0,
    },
    calendarSessionItemFavorite: {
      borderLeftColor: Colors.pink0,
      borderColor: Colors.pink0,
    },
    // New styles: outline based on ticketing type
    calendarSessionItemInternal: {
      borderLeftColor: Colors.yellow0,
      borderColor: Colors.yellow0,
    },
    calendarSessionItemExternal: {
      borderLeftColor: Colors.pink0,
      borderColor: Colors.pink0,
    },
    calendarSessionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    calendarEventTouchable: {
      flex: 1,
      position: 'relative',
      width: '100%',
      height: '100%',
      alignSelf: 'stretch',
    },
    calendarSessionIcon: {
      width: 16,
      height: 16,
      flexShrink: 0,
      minWidth: 16,
      minHeight: 16,
    },
    calendarSessionIconWrapper: {
      width: 16,
      height: 16,
      minWidth: 16,
      minHeight: 16,
      justifyContent: 'center',
      alignItems: 'center',
    },
    calendarSessionTitle: {
      fontSize: isMobile ? 10 : 12,
      flex: 1,
    },
  });

export default withAuthGate(ScheduledSessions);
